import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  ChevronDown,
  ChevronLeft,
  Clipboard,
  FileText,
  Mail,
  MessageSquareText,
  Printer,
} from "lucide-react";
import { Button, getButtonClassName } from "../../../components/Button";
import { Card } from "../../../components/Card";
import { Chip } from "../../../components/Chip";
import { IconButton } from "../../../components/IconButton";
import { Notice } from "../../../components/Notice";
import { ProgressMeter } from "../../../components/ProgressMeter";
import { ReadinessDial } from "../../../components/ReadinessDial";
import { StatusBadge } from "../../../components/StatusBadge";
import { db } from "../../../lib/db";
import { buildMailto, buildSms, formatJobForHandoff } from "../../../lib/handoff";
import { hasLicensedTradeRisk } from "../../../lib/risk";

function formatBudget(job: { budgetMin?: number; budgetMax?: number }): string {
  if (job.budgetMin !== undefined && job.budgetMax !== undefined) {
    return `$${job.budgetMin} to $${job.budgetMax}`;
  }
  if (job.budgetMin !== undefined) {
    return `From $${job.budgetMin}`;
  }
  if (job.budgetMax !== undefined) {
    return `Up to $${job.budgetMax}`;
  }
  return "Not listed";
}

export function JobDetailPage() {
  const { id } = useParams();
  const job = useLiveQuery(() => (id ? db.jobs.get(id) : undefined), [id]);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rawPreview, setRawPreview] = useState(false);

  if (!job) {
    return (
      <Notice title="Job not found" tone="warning">
        This job is not present in local storage. Return to the board and select an available job.
      </Notice>
    );
  }

  const jobBrief = formatJobForHandoff(job);
  const mailto = buildMailto(job, job.contact);
  const sms = buildSms(job, job.contact);
  const licensedRisk = hasLicensedTradeRisk(job.riskFlags);

  async function copyJobBrief() {
    await navigator.clipboard.writeText(jobBrief);
    setCopyStatus("Job brief copied.");
    window.setTimeout(() => setCopyStatus(null), 2500);
  }

  async function copyDetailItem(text: string) {
    await navigator.clipboard.writeText(text);
    setCopyStatus("Detail copied.");
    window.setTimeout(() => setCopyStatus(null), 2500);
  }

  return (
    <div className="space-y-6">
      <Link className={getButtonClassName("ghost", "w-fit", "sm")} to="/jobs">
        <ChevronLeft aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
        Back to all jobs
      </Link>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
        <section className="space-y-5">
          <Card padding="xl" variant="raised">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
              <div className="max-w-3xl">
                <div className="inline-grid gap-2">
                  <p className="page-kicker">{job.category.replace(/_/g, " ")}</p>
                  <span aria-hidden="true" className="h-px w-8 bg-accent-700" />
                </div>
                <h1 className="page-title mt-4 font-semibold">{job.title}</h1>
                <p className="mt-3 max-w-3xl text-base leading-7 text-text-muted">{job.summary}</p>
                <div className="detail-ribbon mt-6 grid gap-0 overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ["Location", `${job.neighborhood}, ${job.city}`],
                    ["Window", job.desiredWindow],
                    ["Urgency", job.urgency],
                    ["Budget", formatBudget(job)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="border-b border-border px-4 py-3 last:border-b-0 sm:border-r sm:last:border-r-0 lg:border-b-0"
                    >
                      <p className="cell-label">{label}</p>
                      <p className="mt-1 text-sm font-semibold text-text">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-hairline p-4">
                <ReadinessDial
                  caption={`${job.scopeItems.filter((item) => item.isVerified).length} of ${job.scopeItems.length} checks`}
                  label="Detail"
                  level={job.readiness.level}
                  score={job.readiness.score}
                  size="lg"
                  secondaryRing
                />
                <div className="mt-4 flex flex-wrap justify-center gap-2 rounded-card border border-border bg-surface-raised p-3 text-sm">
                  <Chip tone="success">
                    Confirmed{" "}
                    <span className="num">
                      {job.scopeItems.filter((item) => item.isVerified).length}
                    </span>
                  </Chip>
                  <Chip tone="warning">
                    Needs answer{" "}
                    <span className="num">
                      {job.scopeItems.filter((item) => !item.isVerified).length}
                    </span>
                  </Chip>
                  <Chip tone="neutral">
                    Total <span className="num">{job.scopeItems.length}</span>
                  </Chip>
                </div>
              </div>
            </div>
          </Card>

          {licensedRisk ? (
            <Notice title="Needs a licensed pro" tone="danger">
              This job contains risk signals that may require licensing, permits, insurance, or
              qualified review. Hire a licensed contractor and verify permits before work starts.
            </Notice>
          ) : null}

          <Card padding="lg" variant="surface">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="section-title font-semibold">Job brief</h2>
              <Button variant="secondary" size="sm" onClick={() => void copyJobBrief()}>
                <Clipboard aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                Copy brief
              </Button>
            </div>
            {copyStatus ? (
              <p
                className="mt-3 rounded-card border border-success-border bg-success-bg px-3 py-2 text-sm font-semibold text-success shadow-xs"
                role="status"
              >
                {copyStatus}
              </p>
            ) : null}
            <div className="mt-5 divide-y divide-border rounded-card border border-border bg-surface-raised">
              {job.scopeItems.map((item) => (
                <div
                  key={item.id}
                  className="grid gap-3 p-4 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] md:items-center"
                >
                  <p className="text-sm font-semibold text-text">{item.category}</p>
                  <p className="text-sm leading-6 text-text-muted">{item.description}</p>
                  <span className="inline-flex items-center justify-end gap-2">
                    <span
                      aria-hidden="true"
                      className={`h-2.5 w-2.5 rounded-pill ${item.isVerified ? "bg-success" : "bg-warning"}`}
                    />
                    <span className="sr-only">
                      {item.isVerified ? "Confirmed" : "Needs answer"}
                    </span>
                    <IconButton
                      icon={<Clipboard aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
                      label={`Copy ${item.category} detail`}
                      size="sm"
                      variant="ghost"
                      onClick={() => void copyDetailItem(item.description)}
                    />
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card padding="lg" variant="surface">
            <h2 className="section-title font-semibold">Things to watch</h2>
            {job.riskFlags.length ? (
              <div className="mt-4 grid gap-3">
                {job.riskFlags.map((flag) => (
                  <article
                    key={flag.type}
                    className="rounded-card border border-warning-border bg-[color-mix(in_srgb,var(--color-warning-bg)_32%,var(--color-paper))] p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-semibold text-text">{flag.label}</h3>
                      <StatusBadge level={flag.severity} tone="glyph" />
                    </div>
                    <p className="mt-2 text-sm leading-6 text-text-muted">{flag.guidance}</p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-text-muted">No warnings found.</p>
            )}
          </Card>

          <Card padding="lg" variant="surface">
            <button
              aria-expanded={previewOpen}
              className="flex w-full items-center justify-between gap-3 text-left text-xl font-semibold text-text"
              type="button"
              onClick={() => setPreviewOpen((open) => !open)}
            >
              Preview brief
              <ChevronDown
                aria-hidden="true"
                className={`h-5 w-5 text-text-subtle transition-transform ${
                  previewOpen ? "rotate-180" : ""
                }`}
                strokeWidth={1.5}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-[var(--duration-spring)] ease-[var(--ease-soft)] ${
                previewOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="overflow-hidden">
                <div className="mt-4 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRawPreview((current) => !current)}
                  >
                    <FileText aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    {rawPreview ? "Document view" : "Raw text"}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => void copyJobBrief()}>
                    <Clipboard aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    Copy preview
                  </Button>
                </div>
                {rawPreview ? (
                  <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded-card border border-border bg-surface-sunken p-4 font-mono text-xs leading-5 text-text-muted">
                    {jobBrief}
                  </pre>
                ) : (
                  <article className="mt-4 rounded-card border border-border bg-paper p-6 shadow-[var(--shadow-key-1)]">
                    <p className="font-sans text-3xl font-semibold leading-tight text-text-strong">
                      {job.title}
                    </p>
                    <p className="mt-4 text-base leading-8 text-text-muted">{job.summary}</p>
                    <div className="mt-5 divide-y divide-border border-y border-border">
                      {job.scopeItems.map((item) => (
                        <div key={item.id} className="py-3">
                          <p className="cell-label">{item.category}</p>
                          <p className="mt-1 text-sm leading-6 text-text-muted">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </article>
                )}
              </div>
            </div>
          </Card>
        </section>

        <aside className="space-y-5 xl:sticky xl:top-24">
          <Card padding="lg" variant="surface">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-text">Detail score</h2>
                <p className="mt-2 text-sm leading-6 text-text-muted">{job.readiness.guidance}</p>
              </div>
              <ReadinessDial
                caption={`${job.scopeItems.filter((item) => item.isVerified).length} of ${job.scopeItems.length} checks`}
                label="Detail"
                level={job.readiness.level}
                score={job.readiness.score}
                size="lg"
              />
            </div>
            <div className="mt-5">
              <ProgressMeter
                label="Detail score"
                tone={
                  job.readiness.level === "GREEN"
                    ? "green"
                    : job.readiness.level === "AMBER"
                      ? "amber"
                      : "red"
                }
                value={job.readiness.score}
              />
            </div>
            {job.readiness.missingItems.length ? (
              <ul className="mt-3 space-y-2 text-sm text-text-muted">
                {job.readiness.missingItems.map((item) => (
                  <li key={item}>
                    <Chip tone="warning">Missing: {item}</Chip>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>

          <Card padding="lg" variant="surface">
            <p className="page-eyebrow">Send</p>
            <h2 className="mt-2 text-xl font-semibold text-text">Contact preferences</h2>
            <p className="mt-2 text-sm leading-6 text-text-muted">
              Use your own mail or SMS app. ClearScope does not message, schedule, charge, or
              arbitrate.
            </p>
            <div className="mt-4 grid gap-3">
              {job.contact.preferredMethod === "SMS" ? (
                <a className={getButtonClassName("primary", "w-full")} href={sms}>
                  <MessageSquareText aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                  Open SMS
                </a>
              ) : (
                <a className={getButtonClassName("primary", "w-full")} href={mailto}>
                  <Mail aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                  Open email
                </a>
              )}
              <a className={getButtonClassName("link", "w-fit")} href={mailto}>
                <Mail aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                Email job brief
              </a>
            </div>
            <dl className="mt-5 space-y-3 text-sm">
              <div>
                <dt className="font-semibold text-text-subtle">Primary</dt>
                <dd className="text-text">{job.contact.primary}</dd>
              </div>
              {job.contact.alternate ? (
                <div>
                  <dt className="font-semibold text-text-subtle">Alternate</dt>
                  <dd className="text-text">{job.contact.alternate}</dd>
                </div>
              ) : null}
              {job.contact.contingency ? (
                <div>
                  <dt className="font-semibold text-text-subtle">Contingency</dt>
                  <dd className="text-text">{job.contact.contingency}</dd>
                </div>
              ) : null}
            </dl>
          </Card>
        </aside>
      </div>
      <div className="no-print mx-auto mt-6 flex w-fit max-w-full flex-wrap items-center justify-center gap-3 rounded-card border border-border bg-surface-raised p-2 shadow-[var(--shadow-3)] md:sticky md:bottom-4 md:z-30">
        <span className="cell-label px-2">Saved locally</span>
        <Button size="md" variant="secondary" onClick={() => void copyJobBrief()}>
          <Clipboard aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
          Copy brief
        </Button>
        <a className={getButtonClassName("primary", "", "md")} href={mailto}>
          <Mail aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
          Open email
        </a>
        <Button size="md" variant="ghost" onClick={() => window.print()}>
          <Printer aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
          Print
        </Button>
      </div>
    </div>
  );
}
